import { fetcher } from '@/data/fetcher';
import { createDataset } from '@/data/mutations';
import {
  invalidateWorkspaceStatsIfNeeded,
  useOrganizationId,
} from '@/hooks/useSwr';
import type {
  DatasetSchema,
  DatasetTransformationSchema,
} from '@/types/schema';
import {
  apiDatasetsPath,
  apiExamplesPath,
  getDatasetJSONLUploadPath,
} from '@/utils/constants';

import { useDatasetDetails } from './useDatasetDetails';
import { useFileUploadTemplateStore } from './useFileUploadTemplateStore';
import { useDatasetSchemaStore } from './useSchemaStore';

const prepareFormData = async (
  includeNewDatasetFields: boolean,
  props: {
    uploadFile: File | null;
    name: string;
    description: string;
    inputSchema?: Record<string, any> | null;
    outputSchema?: Record<string, any> | null;
    transformations?: Record<string, any> | null;
    inputKeys: string[];
    outputKeys: string[];
    metadataKeys: string[];
    inputKeyMappings?: Record<string, string> | null;
    outputKeyMappings?: Record<string, string> | null;
    metadataKeyMappings?: Record<string, string> | null;
  }
) => {
  const {
    uploadFile,
    name,
    description,
    inputSchema,
    outputSchema,
    transformations,
    inputKeys,
    outputKeys,
    metadataKeys,
    inputKeyMappings,
    outputKeyMappings,
    metadataKeyMappings,
  } = props;
  if (!uploadFile) {
    throw new Error('No file selected');
  }
  const fileReader = new FileReader();

  return new Promise<FormData>((resolve, reject) => {
    fileReader.onload = () => {
      try {
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('name', name);
        formData.append('description', description);
        formData.append('data_type', 'kv');

        // Add schema definitions if they exist - FormData expects objects for complex data
        if (includeNewDatasetFields) {
          if (inputSchema && Object.keys(inputSchema).length > 0) {
            formData.append(
              'inputs_schema_definition',
              JSON.stringify(inputSchema)
            );
          }
          if (outputSchema && Object.keys(outputSchema).length > 0) {
            formData.append(
              'outputs_schema_definition',
              JSON.stringify(outputSchema)
            );
          }
          if (transformations && transformations.length > 0) {
            formData.append('transformations', JSON.stringify(transformations));
          }
        }

        if (!inputKeys?.length) {
          return reject(new Error('You must specify at least one input key'));
        }

        for (const key of inputKeys) {
          formData.append('input_keys', key);
        }
        for (const key of outputKeys ?? []) {
          formData.append('output_keys', key);
        }
        for (const key of metadataKeys ?? []) {
          formData.append('metadata_keys', key);
        }

        // Add key mappings if they exist
        if (inputKeyMappings && Object.keys(inputKeyMappings).length > 0) {
          formData.append(
            'input_key_mappings',
            JSON.stringify(inputKeyMappings)
          );
        }
        if (outputKeyMappings && Object.keys(outputKeyMappings).length > 0) {
          formData.append(
            'output_key_mappings',
            JSON.stringify(outputKeyMappings)
          );
        }
        if (
          metadataKeyMappings &&
          Object.keys(metadataKeyMappings).length > 0
        ) {
          formData.append(
            'metadata_key_mappings',
            JSON.stringify(metadataKeyMappings)
          );
        }

        return resolve(formData);
      } catch (error) {
        return reject(error);
      }
    };

    fileReader.onerror = () => reject(fileReader.error);
    fileReader.readAsText(uploadFile);
  });
};

const createDatasetAndUploadJSONL = async (
  formData: FormData,
  workspaceId: string | null,
  inputSchema: Record<string, unknown> | null,
  outputSchema: Record<string, unknown> | null,
  transformations?: DatasetTransformationSchema[] | null
): Promise<DatasetSchema> => {
  if (!workspaceId) {
    throw new Error('Workspace ID is not found');
  }
  const dataset = await createDataset(workspaceId, {
    name: formData.get('name') as string,
    description: formData.get('description') as string,
    data_type: 'kv',
    inputs_schema_definition: inputSchema,
    outputs_schema_definition: outputSchema,
    transformations,
    extra: {
      source: 'upload',
    },
  });

  try {
    await fetcher<DatasetSchema>({
      url: getDatasetJSONLUploadPath(dataset.id),
      method: 'POST',
      body: formData,
      headers: { 'X-Tenant-Id': workspaceId },
    });

    // Invalidate workspace stats after successful upload
    invalidateWorkspaceStatsIfNeeded(apiDatasetsPath, 'POST');

    return dataset;
  } catch (error) {
    await fetcher({
      url: `${apiDatasetsPath}/${dataset.id}`,
      method: 'DELETE',
      headers: { 'X-Tenant-Id': workspaceId },
    });

    // Invalidate workspace stats after cleanup deletion
    invalidateWorkspaceStatsIfNeeded(
      `${apiDatasetsPath}/${dataset.id}`,
      'DELETE'
    );

    throw error;
  }
};

export const useUploadFile = () => {
  const workspaceId = useOrganizationId();

  const name = useDatasetDetails((state) => state.name);
  const description = useDatasetDetails((state) => state.description);
  const uploadFile = useFileUploadTemplateStore((state) => state.file);
  const inputKeys = useFileUploadTemplateStore((state) => state.inputKeys);
  const outputKeys = useFileUploadTemplateStore((state) => state.outputKeys);
  const metadataKeys = useFileUploadTemplateStore(
    (state) => state.metadataKeys
  );
  const inputKeyMappings = useFileUploadTemplateStore(
    (state) => state.inputKeyMappings
  );
  const outputKeyMappings = useFileUploadTemplateStore(
    (state) => state.outputKeyMappings
  );
  const metadataKeyMappings = useFileUploadTemplateStore(
    (state) => state.metadataKeyMappings
  );

  const isCSV = uploadFile?.name.toLowerCase().endsWith('.csv');
  const isJSONL = uploadFile?.name.toLowerCase().endsWith('.jsonl');

  // Schema store values
  const inputSchema = useDatasetSchemaStore((state) => state.inputSchema);
  const outputSchema = useDatasetSchemaStore((state) => state.outputSchema);
  const transformations = useDatasetSchemaStore(
    (state) => state.transformations
  );

  const upload = async () => {
    const formData = await prepareFormData(
      // Don't include new dataset fields in FormData for JSONL uploads
      // JSONL creates and uploads datasets in two steps
      isCSV ? true : false,
      {
        uploadFile,
        name,
        description,
        inputSchema,
        outputSchema,
        transformations,
        inputKeys: inputKeys ?? [],
        outputKeys: outputKeys ?? [],
        metadataKeys: metadataKeys ?? [],
        inputKeyMappings,
        outputKeyMappings,
        metadataKeyMappings,
      }
    );
    if (isCSV) {
      return fetcher<DatasetSchema>({
        url: `${apiDatasetsPath}/upload`,
        method: 'POST',
        body: formData,
        headers: { 'X-Tenant-Id': workspaceId },
      });
    } else if (isJSONL) {
      return createDatasetAndUploadJSONL(
        formData,
        workspaceId,
        inputSchema,
        outputSchema,
        transformations
      );
    } else {
      throw new Error('Invalid file extension. Must be .csv or .jsonl');
    }
  };

  const uploadToExistingDataset = async (datasetId: string) => {
    const formData = await prepareFormData(false, {
      uploadFile,
      name,
      description,
      inputKeys: inputKeys ?? [],
      outputKeys: outputKeys ?? [],
      metadataKeys: metadataKeys ?? [],
      inputKeyMappings,
      outputKeyMappings,
      metadataKeyMappings,
    });
    if (isCSV) {
      return fetcher({
        url: `${apiExamplesPath}/upload/${datasetId}`,
        method: 'POST',
        body: formData,
        headers: { 'X-Tenant-Id': workspaceId },
      });
    } else if (isJSONL) {
      return fetcher({
        url: getDatasetJSONLUploadPath(datasetId),
        method: 'POST',
        body: formData,
        headers: { 'X-Tenant-Id': workspaceId },
      });
    } else {
      throw new Error('Invalid file extension. Must be .csv or .jsonl');
    }
  };

  return { upload, uploadToExistingDataset };
};
